import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Check, ArrowRight, Utensils, Home, PawPrint, Music, Heart, HandHeart, Compass, BookOpen, Send, MessageCircle, Quote, Star, Users, TrendingUp } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";
const HERO_BG = "/hero_bg.jpg";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function LandingPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#110A06]">
      <Header />
      <HeroSection user={user} />
      <RealityContrastSection />
      <StartSimpleSection />
      <HowItWorksSection />
      <PricingSection user={user} />
      <MissionSection />
      <SocialProofSection />
      <FinalCTASection />
      <ContactSection />
      <Footer />
    </div>
  );
}

/* ─── HERO ─── */
function HeroSection({ user }) {
  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#110A06] via-[#110A06]/90 to-[#110A06]/60" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-2xl">
          <motion.h1 variants={fadeUp} className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-[#F5E6C8] mb-5">
            Own Your <span className="text-[#D4A54A]">Karma.</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="font-serif text-xl sm:text-2xl text-[#D4A54A]/80 mb-8 italic">
            Start doing real, meaningful karma — with guidance and ease.
          </motion.p>
          <motion.p variants={fadeUp} className="font-sans text-base sm:text-lg leading-relaxed text-[#F5E6C8]/80 mb-10 max-w-lg">
            Most people donate. Some people pray.<br />
            <span className="text-[#F5E6C8] font-medium">A few choose to do karma themselves.</span>
          </motion.p>
          <motion.div variants={fadeUp}>
            {user ? (
              <Link to="/dashboard" data-testid="hero-dashboard-btn" className="inline-flex items-center gap-2 bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-8 py-4 font-sans font-bold text-lg transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#D4A54A]/20">
                Go to Dashboard <ArrowRight className="w-5 h-5" />
              </Link>
            ) : (
              <a href="https://wa.me/918800461522?text=START" target="_blank" rel="noopener noreferrer" data-testid="hero-get-started-btn" className="inline-flex items-center gap-2 bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-8 py-4 font-sans font-bold text-lg transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#D4A54A]/20">
                Start Your First Karma <ArrowRight className="w-5 h-5" />
              </a>
            )}
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="hidden lg:flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-[#D4A54A]/10 rounded-full blur-[60px] scale-110" />
            <img src={LOGO_URL} alt="KarmaCircle" className="relative w-72 h-72 rounded-full object-cover ring-2 ring-[#D4A54A]/30 shadow-2xl shadow-[#D4A54A]/10" />
          </div>
        </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── REALITY CONTRAST ─── */
function RealityContrastSection() {
  return (
    <section className="py-24 md:py-32 bg-[#110A06] relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-px bg-gradient-to-r from-transparent via-[#D4A54A]/30 to-transparent" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left — What usually happens */}
          <motion.div variants={fadeUp} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-8">
            <p className="font-sans text-xs uppercase tracking-[0.3em] font-bold text-[#A89070]/60 mb-5">What usually happens</p>
            <div className="space-y-5">
              {["Donate and move on", "Plan to do good someday", "Stay busy"].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-[#3D2D1D]/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#A89070]/40 font-sans text-lg">-</span>
                  </div>
                  <p className="font-sans text-base text-[#A89070]/70 line-through decoration-[#A89070]/30">{item}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right — What changes here */}
          <motion.div variants={fadeUp} className="bg-[#1A1008] rounded-2xl border border-[#D4A54A]/30 p-8 gold-glow">
            <p className="font-sans text-xs uppercase tracking-[0.3em] font-bold text-[#D4A54A] mb-5">What changes here</p>
            <div className="space-y-5">
              {["Take action", "Show up", "Experience it yourself"].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-[#D4A54A]/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-[#D4A54A]" strokeWidth={2.5} />
                  </div>
                  <p className="font-sans text-base text-[#F5E6C8] font-medium">{item}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── START SIMPLE ─── */
function StartSimpleSection() {
  const cards = [
    { icon: Utensils, title: "Feed people", color: "#D4A54A" },
    { icon: Home, title: "Visit old age home", color: "#7D9B6A" },
    { icon: PawPrint, title: "Animal seva", color: "#C49540" },
    { icon: Heart, title: "Temple seva", color: "#8B6914" },
    { icon: Music, title: "Attend Kirtan", color: "#D4A54A" },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#0A0604] relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-px bg-gradient-to-r from-transparent via-[#D4A54A]/30 to-transparent" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.3em] font-bold text-[#D4A54A] mb-3">Start Simple</motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8]">Pick one. We'll handle the rest.</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {cards.map((c, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6 text-center hover:border-[#D4A54A]/30 hover:-translate-y-1 transition-all duration-300 group cursor-default">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-colors" style={{ backgroundColor: c.color + '15' }}>
                <c.icon className="w-7 h-7" style={{ color: c.color }} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-base font-semibold text-[#F5E6C8]">{c.title}</h3>
            </motion.div>
          ))}
        </motion.div>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-sans text-base text-[#D4A54A] text-center mt-10 font-medium italic">
          We guide you step-by-step.
        </motion.p>
      </div>
    </section>
  );
}

/* ─── PRICING ─── */
function PricingSection({ user }) {
  const tiers = [
    {
      name: "Karma Assist",
      tag: "Perfect for people who want to do karma regularly but need guidance.",
      price: "399",
      color: "#7D9B6A",
      features: [
        "Personalized karma suggestions every month",
        "Simple acts of service you can do yourself",
        "Step-by-step guidance and reminders",
        "WhatsApp support when needed",
      ],
      cta: "You do the karma. We guide you.",
      btnText: "Start Your First Karma",
      tier: "karma_assist"
    },
    {
      name: "Karma Circle",
      tag: "Join meaningful group karma experiences. Just show up.",
      price: "1,999",
      color: "#D4A54A",
      features: [
        "Organized food distribution (Bhandara)",
        "Visits to old-age homes & orphanages",
        "Tree plantation & community service drives",
        "Group seva activities with like-minded people",
        "End-to-end planning handled by us",
      ],
      cta: "You participate. We manage everything.",
      btnText: "Join Your First Experience",
      popular: true,
      tier: "karma_circle"
    },
    {
      name: "Personalized Karma",
      tag: "Private spiritual and karma solutions for you and your family.",
      price: "Custom",
      color: "#F5E6C8",
      features: [
        "Astrology consultation & guidance",
        "Personalized karma recommendations",
        "End-to-end organization of Bhandara, Pooja, Kirtan etc.",
        "Priest, venue, material & logistics support",
      ],
      cta: "You share your requirement. We handle the entire process.",
      btnText: "Contact Us",
      tier: "personalized"
    },
  ];

  return (
    <section id="pricing" className="py-24 md:py-32 bg-[#110A06] relative" data-testid="pricing-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-px bg-gradient-to-r from-transparent via-[#D4A54A]/30 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.3em] font-bold text-[#D4A54A] mb-3">Choose Your Path</motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8]">Plans That Fit Your Journey</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier, i) => (
            <motion.div key={i} variants={fadeUp} data-testid={`pricing-card-${tier.tier}`}
              className={`bg-[#1A1008] rounded-3xl border ${tier.popular ? 'border-[#D4A54A] gold-glow' : 'border-[#3D2D1D]/50'} p-8 relative hover:border-[#D4A54A]/50 transition-all duration-300`}>
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A54A] text-[#110A06] text-xs font-sans font-bold uppercase tracking-wider px-4 py-1 rounded-full">Most Popular</div>
              )}
              <div className="mb-5">
                <div className="w-3 h-3 rounded-full mb-4" style={{ backgroundColor: tier.color }} />
                <h3 className="font-serif text-2xl font-semibold text-[#F5E6C8] mb-1">{tier.name}</h3>
                <p className="font-sans text-sm text-[#A89070]">{tier.tag}</p>
              </div>
              <div className="mb-6">
                {tier.price === "Custom" ? (
                  <span className="font-serif text-3xl font-semibold text-[#F5E6C8]">Custom Pricing</span>
                ) : (
                  <div className="flex items-baseline gap-1">
                    <span className="font-serif text-4xl font-semibold text-[#D4A54A]">{'\u20B9'}{tier.price}</span>
                    <span className="font-sans text-sm text-[#A89070]">/month</span>
                  </div>
                )}
              </div>
              <ul className="space-y-3 mb-8">
                {tier.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#D4A54A] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span className="font-sans text-sm text-[#A89070]">{f}</span>
                  </li>
                ))}
              </ul>
              {tier.cta && <p className="font-sans text-sm text-[#D4A54A] italic mb-6">{tier.cta}</p>}
              {tier.price === "Custom" ? (
                <a href="https://wa.me/918800461522?text=START" target="_blank" rel="noopener noreferrer" data-testid={`pricing-btn-${tier.tier}`} className="block text-center border border-[#F5E6C8]/30 text-[#F5E6C8] hover:bg-[#F5E6C8]/10 rounded-full px-8 py-3 font-sans font-bold transition-all duration-200">
                  {tier.btnText}
                </a>
              ) : (
                <a href="https://wa.me/918800461522?text=START" target="_blank" rel="noopener noreferrer" data-testid={`pricing-btn-${tier.tier}`}
                  className={`block text-center rounded-full px-8 py-3 font-sans font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${
                    tier.popular ? 'bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] hover:shadow-[#D4A54A]/20' : 'bg-[#7D9B6A] hover:bg-[#6B8A58] text-white'
                  }`}>
                  {tier.btnText}
                </a>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── OUR MISSION ─── */
function MissionSection() {
  return (
    <section className="py-24 md:py-32 bg-[#0A0604] relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-px bg-gradient-to-r from-transparent via-[#D4A54A]/30 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="md:col-span-5">
            <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.3em] font-bold text-[#D4A54A] mb-3">Our Mission</motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8] mb-6">
              Karma is owned by the individual — not something you outsource or do online
            </motion.h2>
            <motion.p variants={fadeUp} className="font-sans text-base leading-relaxed text-[#A89070] mb-4">
              In a world of convenience, we've started outsourcing even our good deeds. Donating money is wonderful, but there's something irreplaceable about being there — feeding someone with your own hands, sitting with the elderly, caring for animals.
            </motion.p>
            <motion.p variants={fadeUp} className="font-sans text-base leading-relaxed text-[#A89070]">
              KarmaCircle bridges that gap. We don't just tell you what to do — we guide you through it, connect you with others on the same path, and make sure every act of karma is real, meaningful, and personal.
            </motion.p>
          </motion.div>
          <div className="md:col-span-7 grid grid-cols-2 gap-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="rounded-2xl overflow-hidden border border-[#3D2D1D]/30">
              <img src="/mission1.jpg" alt="Caring for elderly" className="w-full h-64 object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="rounded-2xl overflow-hidden mt-8 border border-[#3D2D1D]/30">
              <img src="/mission2.jpg" alt="Feeding children" className="w-full h-64 object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="rounded-2xl overflow-hidden border border-[#3D2D1D]/30">
              <img src="/mission3.jpg" alt="Helping with love" className="w-full h-56 object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="rounded-2xl overflow-hidden border border-[#3D2D1D]/30">
              <img src="/mission4.jpg" alt="Animal seva" className="w-full h-56 object-cover" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── SOCIAL PROOF ─── */
function SocialProofSection() {
  const stats = [
    { value: "50+", label: "Karmas Completed", icon: Star },
    { value: "100+", label: "Lives Impacted", icon: Heart },
    { value: "15+", label: "Group Events", icon: Users },
  ];

  const testimonials = [
    { text: "I always wanted to do something meaningful but never knew where to start. KarmaCircle made it so simple — my first bhandara was life-changing.", name: "Priya M." },
    { text: "The group experience was incredible. Feeding 200 people with strangers who became friends — that's the power of doing karma together.", name: "Rahul S." },
    { text: "I've donated money before, but actually being there, serving food with my own hands... there's no comparison. This is real karma.", name: "Anita K." },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#0A0604] relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-px bg-gradient-to-r from-transparent via-[#D4A54A]/30 to-transparent" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-3 gap-6 mb-16">
          {stats.map((s, i) => (
            <motion.div key={i} variants={fadeUp} className="text-center">
              <s.icon className="w-6 h-6 text-[#D4A54A] mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-serif text-4xl sm:text-5xl font-semibold text-[#D4A54A]">{s.value}</p>
              <p className="font-sans text-sm text-[#A89070] mt-1">{s.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Testimonials */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-7 hover:border-[#D4A54A]/20 transition-all duration-300">
              <Quote className="w-6 h-6 text-[#D4A54A]/30 mb-4" />
              <p className="font-sans text-sm leading-relaxed text-[#A89070] mb-5 italic">"{t.text}"</p>
              <div>
                <p className="font-sans text-sm font-semibold text-[#F5E6C8]">{t.name}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── HOW IT WORKS ─── */
function HowItWorksSection() {
  const steps = [
    { num: "01", word: "Choose", desc: "Pick a karma that speaks to you" },
    { num: "02", word: "We guide", desc: "Get clear, step-by-step direction" },
    { num: "03", word: "You do", desc: "Complete it in real life" },
    { num: "04", word: "You experience", desc: "Feel the impact firsthand" },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#110A06] relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-px bg-gradient-to-r from-transparent via-[#D4A54A]/30 to-transparent" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.3em] font-bold text-[#D4A54A] mb-3">The Process</motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8]">How It Works</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-7 text-center hover:border-[#D4A54A]/30 transition-all duration-300 group relative">
              <div className="font-serif text-4xl font-bold text-[#D4A54A]/15 mb-2 group-hover:text-[#D4A54A]/30 transition-colors">{s.num}</div>
              <h3 className="font-serif text-xl font-semibold text-[#D4A54A] mb-2">{s.word}</h3>
              <p className="font-sans text-sm text-[#A89070]">{s.desc}</p>
              {i < 3 && <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-[#3D2D1D]" />}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── FINAL CTA ─── */
function FinalCTASection() {
  return (
    <section className="py-24 md:py-32 bg-[#0A0604] relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-px bg-gradient-to-r from-transparent via-[#D4A54A]/30 to-transparent" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F5E6C8] mb-6">
            Start Your First Karma<br /><span className="text-[#D4A54A]">This Week</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="font-sans text-base text-[#A89070] mb-10 max-w-lg mx-auto">
            No overthinking. No waiting. Just one real, guided action that makes a difference.
          </motion.p>
          <motion.div variants={fadeUp} className="flex justify-center">
            <a href="https://wa.me/918800461522?text=START" target="_blank" rel="noopener noreferrer" data-testid="final-cta-whatsapp"
              className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full px-8 py-4 font-sans font-bold text-lg transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#25D366]/20">
              <MessageCircle className="w-5 h-5" /> Message on WhatsApp
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── CONTACT ─── */
function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Message sent! We\'ll get back to you soon.');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch { toast.error('Failed to send message.'); }
    finally { setSending(false); }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#110A06] relative" data-testid="contact-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-px bg-gradient-to-r from-transparent via-[#D4A54A]/30 to-transparent" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.3em] font-bold text-[#D4A54A] mb-3">Get In Touch</motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8]">Have Questions?</motion.h2>
        </motion.div>
        <motion.form initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} onSubmit={handleSubmit} className="bg-[#1A1008] rounded-3xl border border-[#3D2D1D]/50 p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 block">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 focus:border-[#D4A54A] placeholder:text-[#5C4830]" placeholder="Your name" />
            </div>
            <div>
              <label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 block">Email</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 focus:border-[#D4A54A] placeholder:text-[#5C4830]" placeholder="you@example.com" />
            </div>
          </div>
          <div>
            <label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 block">Phone</label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 focus:border-[#D4A54A] placeholder:text-[#5C4830]" placeholder="+91 9876543210" />
          </div>
          <div>
            <label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 block">Message</label>
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="contact-message" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] min-h-[120px] focus:border-[#D4A54A] placeholder:text-[#5C4830]" placeholder="Tell us about your karma goals..." />
          </div>
          <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="w-full bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-8 py-3 font-sans font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4A54A]/20 disabled:opacity-60 flex items-center justify-center gap-2">
            <Send className="w-4 h-4" /> {sending ? 'Sending...' : 'Send Message'}
          </button>
        </motion.form>
      </div>
    </section>
  );
}

/* ─── FOOTER ─── */
function Footer() {
  return (
    <footer className="bg-[#0A0604] border-t border-[#3D2D1D]/30 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="KarmaCircle" className="w-8 h-8 rounded-full object-cover ring-1 ring-[#D4A54A]/30" />
            <span className="font-serif text-lg font-semibold text-[#D4A54A]">KarmaCircle</span>
          </div>
          <p className="font-sans text-sm text-[#A89070]/60">Own your karma. We guide you.</p>
          <div className="flex gap-6">
            <a href="#pricing" className="font-sans text-sm text-[#A89070]/60 hover:text-[#D4A54A] transition-colors">Pricing</a>
            <a href="#contact" className="font-sans text-sm text-[#A89070]/60 hover:text-[#D4A54A] transition-colors">Contact</a>
            <a href="https://wa.me/918800461522" target="_blank" rel="noopener noreferrer" className="font-sans text-sm text-[#A89070]/60 hover:text-[#25D366] transition-colors">WhatsApp</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
